/**
 * Simple event emitter for handling checkout events
 * Supports subscribing, unsubscribing, and emitting events
 */
export class Emitter<T extends string = string> {
  private handlers = new Map<T, Set<Function>>();

  /**
   * Subscribe to an event
   * @param event - Event name to listen to
   * @param handler - Callback function to execute when event is emitted
   * @returns Unsubscribe function
   */
  on(event: T, handler: Function): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Subscribe to an event that auto-unsubscribes after first emission
   *
   * Common use case: waiting for 'loaded' event or handling first 'success'.
   * Without this helper, developers would need to manually unsubscribe inside
   * their handler, which is error-prone and leads to memory leaks if forgotten.
   *
   * @param event - Event name to listen to
   * @param handler - Callback function to execute when event is emitted
   * @returns Unsubscribe function
   */
  once(event: T, handler: Function): () => void {
    const wrappedHandler = (...args: any[]) => {
      handler(...args);
      this.off(event, wrappedHandler);
    };
    return this.on(event, wrappedHandler);
  }

  /**
   * Unsubscribe from an event
   * @param event - Event name to stop listening to
   * @param handler - Callback function to remove
   */
  off(event: T, handler: Function): void {
    this.handlers.get(event)?.delete(handler);
  }

  /**
   * Emit an event with optional arguments
   * @param event - Event name to emit
   * @param args - Arguments to pass to event handlers
   */
  emit(event: T, ...args: any[]): void {
    this.handlers.get(event)?.forEach(h => {
      try {
        h(...args);
      } catch (err) {
        console.error(`[Emitter] Error in ${event} handler:`, err);
      }
    });
  }

  /**
   * Emit an event but rethrow captured listener errors after every listener runs.
   * This keeps side effects from one handler from preventing others while still
   * allowing callers to apply hard-fail handling when they need deterministic
   * settle behaviour.
   */
  emitAndThrow(event: T, ...args: any[]): void {
    let capturedError: unknown;
    this.handlers.get(event)?.forEach((h) => {
      try {
        h(...args);
      } catch (error) {
        if (capturedError === undefined) {
          capturedError = error;
        }
      }
    });
    if (capturedError !== undefined) {
      if (capturedError instanceof Error) throw capturedError;
      throw new Error(String(capturedError));
    }
  }

  /**
   * Returns true if at least one handler is registered for the given event
   */
  hasListeners(event: T): boolean {
    const set = this.handlers.get(event);
    return !!set && set.size > 0;
  }

  /**
   * Clear all event handlers
   */
  clear(): void {
    this.handlers.clear();
  }
}
